/**
 * ContactUsModal.tsx
 * Modal for users to submit contact messages with file attachment support
 */

import React, { useState, useEffect } from 'react';
import { X, Send, Upload, Mail, Phone, User, FileText, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiEndpoint } from '@/config/api';
import { trackModalView } from '../utils/visitorTracking';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function ContactUsModal({ open, onClose }: Props) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: '',
    preferred_contact: 'email',
    best_time: 'morning',
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Track modal view when opened
  useEffect(() => {
    if (open) {
      trackModalView('Contact Us Modal');
    }
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Limit file size to 5MB
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setAttachment(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.email.trim() && !formData.mobile.trim()) {
      toast.error('Please provide either email or mobile number');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter your message');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('mobile', formData.mobile);
      submitData.append('subject', formData.subject);
      submitData.append('message', formData.message);
      submitData.append('preferred_contact', formData.preferred_contact);
      submitData.append('best_time', formData.best_time);
      
      if (attachment) {
        submitData.append('attachment', attachment);
      }

      const response = await fetch(getApiEndpoint('/contact/submit'), {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Message sent successfully! We will get back to you soon.');
        setSubmitted(true);
        
        // Reset form after 2 seconds and close
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            mobile: '',
            subject: '',
            message: '',
            preferred_contact: 'email',
            best_time: 'morning',
          });
          setAttachment(null);
          setSubmitted(false);
          onClose();
        }, 2000);
      } else {
        toast.error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Error sending message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        mobile: '',
        subject: '',
        message: '',
        preferred_contact: 'email',
        best_time: 'morning',
      });
      setAttachment(null);
      setSubmitted(false);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Contact us modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
    >
      {/* Backdrop */}
      <div
        onClick={handleClose}
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-[#061a44] border border-white/10 rounded-3xl shadow-[0_40px_100px_-50px_rgba(11,59,130,0.45)] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/15 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">Contact Us</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto bg-[#07193d] text-slate-100">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#ffd9a6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#ff2d87]" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">Message Sent!</h3>
              <p className="text-slate-300">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  <User className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                  Name <span className="text-[#ff2d87]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Email and Mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    <Mail className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    <Phone className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                    Mobile
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                    placeholder="+91 94917 02821"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  <FileText className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                  placeholder="Brief subject of your message"
                />
              </div>

              {/* Preferred Contact Method */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    Preferred Contact Method
                  </label>
                  <select
                    name="preferred_contact"
                    value={formData.preferred_contact}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    <Clock className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                    Best Time to Contact
                  </label>
                  <select
                    name="best_time"
                    value={formData.best_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent"
                  >
                    <option value="morning">Morning (9 AM - 12 PM)</option>
                    <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                    <option value="evening">Evening (4 PM - 8 PM)</option>
                    <option value="anytime">Anytime</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Message <span className="text-[#ff2d87]">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-4 py-2 bg-[#0d234e] border border-white/10 rounded-2xl text-slate-100 placeholder:text-slate-500 focus:ring-2 focus:ring-[#ff2d87] focus:border-transparent resize-none"
                  placeholder="Tell us how we can help you..."
                  required
                />
              </div>

              {/* File Attachment */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  <Upload className="w-4 h-4 inline mr-1 text-[#ffd9a6]" />
                  Attachment (Optional)
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    />
                    <div className="px-4 py-2 border-2 border-white/10 rounded-2xl hover:border-[#ff2d87] transition-colors text-center bg-white/5">
                      {attachment ? (
                        <span className="text-sm text-slate-100">
                          📎 {attachment.name} ({(attachment.size / 1024).toFixed(1)} KB)
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">Click to upload file (Max 5MB)</span>
                      )}
                    </div>
                  </label>
                  {attachment && (
                    <button
                      type="button"
                      onClick={() => setAttachment(null)}
                      className="px-3 py-2 text-sm text-[#ffd9a6] bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-6 py-3 border border-white/10 text-slate-200 rounded-2xl hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#5f1c7c] via-[#0b3b82] to-[#ff2d87] text-white rounded-2xl hover:from-[#5c1b76] hover:to-[#f12c7f] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
